import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, UserModel, permisosModel } from './_models'
import * as authHelper from './AuthHelpers'
import { getUserByToken } from './_requests'
import { WithChildren } from '../../../../_metronic/helpers'
import { useAuth0 } from "@auth0/auth0-react";

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentRol: permisosModel | undefined
  setcurrentRol: Dispatch<SetStateAction<permisosModel | undefined>>
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentRol: undefined,
  setcurrentRol: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: () => { },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [currentRol, setcurrentRol] = useState<permisosModel | undefined>()
  const { logout: auth0Logout } = useAuth0();

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    // Elimina el token almacenado con saveAuth
    saveAuth(undefined);
  
    // Limpia el estado del usuario y los roles
    setCurrentUser(undefined);
    setcurrentRol(undefined);
  
    // Vacía completamente el localStorage
    localStorage.clear();
  
    // Utiliza la nueva variable de entorno para la URL de redirección
    const returnToUrl = process.env.REACT_APP_LOGOUT_REDIRECT_URI;
  
    auth0Logout({
      logoutParams: { returnTo: returnToUrl }, // Redirige a la URL definida
    });
  };
  
  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, currentRol, setcurrentRol, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser, saveAuth } = useAuth()
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    const fetchAuth = async () => {
      try {
        if (isAuthenticated) {
          const token = await getAccessTokenSilently();
          console.log("Token obtenido: ", token);
          saveAuth({ api_token: token })
        }
      } catch (error) {
        console.error("Error obteniendo el token", error)
        logout()
      }
    }

    const fetchUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const { data } = await getUserByToken(apiToken)
          if (data) {
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error("Error obteniendo el usuario", error)
        logout()
      } finally {
        setShowSplashScreen(false)
      }
    }

    if (auth && auth.api_token) {
      fetchUser(auth.api_token)
    } else {
      fetchAuth().finally(() => setShowSplashScreen(false))
    }

    // eslint-disable-next-line
  }, [isAuthenticated, auth])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
